import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock,
  Send,
  MessageCircle,
  Globe,
  ArrowRight,
  Loader,
  CheckCircle,
  AlertCircle,
  ExternalLink
} from 'lucide-react'
import apiService from '../../services/api'
import blockchainService from '../../services/blockchain'

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    department: 'general'
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [submitMessage, setSubmitMessage] = useState('')
  const [useBlockchain, setUseBlockchain] = useState(false)
  const [walletConnected, setWalletConnected] = useState(false)
  const [blockchainAvailable, setBlockchainAvailable] = useState(false)

  // 检查区块链服务是否可用
  React.useEffect(() => {
    try {
      if (blockchainService) {
        setBlockchainAvailable(true)
        console.log('✅ 区块链服务可用')
      } else {
        setBlockchainAvailable(false)
        console.log('❌ 区块链服务不可用')
      }
    } catch (error) {
      console.error('区块链服务检查失败:', error)
      setBlockchainAvailable(false)
    }
  }, [])

  const contactInfo = [
    {
      icon: Mail,
      title: '邮箱',
      value: 'contact@deeplink.hk',
      description: '发送邮件给我们'
    },
    {
      icon: Phone,
      title: '电话',
      value: '+852 1234-5678',
      description: '工作日 9:00-18:00'
    },
    {
      icon: MapPin,
      title: '地址',
      value: '香港中环国际金融中心二期',
      description: '欢迎来访'
    },
    {
      icon: Clock,
      title: '工作时间',
      value: '周一至周五 9:00-18:00',
      description: '节假日除外'
    }
  ]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleConnectWallet = async () => {
    try {
      // 检查区块链服务是否可用
      if (!blockchainService) {
        throw new Error('区块链服务不可用')
      }
      
      await blockchainService.connectWallet()
      setWalletConnected(true)
      setSubmitMessage('钱包连接成功！')
    } catch (error) {
      console.error('钱包连接失败:', error)
      setSubmitMessage('钱包连接失败：' + (error as Error).message)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setSubmitMessage('')

    try {
      if (useBlockchain && walletConnected) {
        // 提交到区块链
        try {
          const result = await blockchainService.createContactOnChain({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: `${formData.subject}: ${formData.message}`
          })
          
          setSubmitStatus('success')
          setSubmitMessage(`消息已上链！交易哈希: ${result.txHash}`)
        } catch (blockchainError) {
          console.error('区块链提交失败:', blockchainError)
          setSubmitStatus('error')
          setSubmitMessage('区块链提交失败：' + (blockchainError as Error).message)
          return
        }
      } else {
        // 提交到传统API
        await apiService.submitContactForm(formData)
        setSubmitStatus('success')
        setSubmitMessage('消息提交成功！我们会尽快回复您。')
      }

      // 重置表单
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        department: 'general'
      })
    } catch (error) {
      setSubmitStatus('error')
      setSubmitMessage('提交失败：' + (error as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const departments = [
    {
      name: '商务合作',
      email: 'business@deeplink.hk',
      description: '产品合作、渠道合作、战略合作'
    },
    {
      name: '技术支持',
      email: 'support@deeplink.hk',
      description: '技术咨询、产品支持、开发合作'
    },
    {
      name: '媒体合作',
      email: 'media@deeplink.hk',
      description: '媒体采访、新闻发布、品牌合作'
    },
    {
      name: '投资合作',
      email: 'investment@deeplink.hk',
      description: '投资咨询、项目合作、融资相关'
    }
  ]

  return (
    <>
      <Helmet>
        <title>联系我们 - DeepLink Limited</title>
        <meta name="description" content="DeepLink Limited联系方式，包括邮箱、电话、地址等信息。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-teal-900 via-blue-900 to-indigo-900">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                联系我们
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
                我们很乐意听到您的声音，随时为您提供帮助
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                联系方式
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                选择最适合的方式与我们取得联系
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {contactInfo.map((info, index) => {
                const Icon = info.icon
                return (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      {info.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 font-semibold mb-2">
                      {info.value}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                      {info.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  发送消息
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                  填写下面的表单，我们会尽快回复您
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 区块链选项 */}
                  {blockchainAvailable && (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <Globe className="w-5 h-5 text-purple-600" />
                          <span className="font-semibold text-gray-900 dark:text-white">区块链提交</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={useBlockchain}
                            onChange={(e) => setUseBlockchain(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                        </label>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        启用区块链提交，您的消息将被永久记录在区块链上，确保数据不可篡改。
                      </p>
                      {useBlockchain && !walletConnected && (
                        <button
                          type="button"
                          onClick={handleConnectWallet}
                          className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 transition-colors flex items-center"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          连接钱包
                        </button>
                      )}
                      {useBlockchain && walletConnected && (
                        <div className="flex items-center text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          钱包已连接
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        姓名 *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        邮箱 *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      电话
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                      placeholder="请输入您的电话"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      主题 *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                    >
                      <option value="">请选择主题</option>
                      <option value="产品咨询">产品咨询</option>
                      <option value="商务合作">商务合作</option>
                      <option value="技术支持">技术支持</option>
                      <option value="投资合作">投资合作</option>
                      <option value="媒体合作">媒体合作</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      消息 *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                      placeholder="请输入您的消息内容"
                    ></textarea>
                  </div>

                  {/* 提交状态提示 */}
                  {submitMessage && (
                    <div className={`p-4 rounded-lg flex items-center ${
                      submitStatus === 'success' 
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                        : submitStatus === 'error'
                        ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                        : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                    }`}>
                      {submitStatus === 'success' ? (
                        <CheckCircle className="w-5 h-5 mr-2" />
                      ) : submitStatus === 'error' ? (
                        <AlertCircle className="w-5 h-5 mr-2" />
                      ) : (
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                      )}
                      {submitMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || (useBlockchain && !walletConnected)}
                    className="w-full bg-gradient-to-r from-teal-600 to-blue-600 text-white px-6 py-4 rounded-lg font-semibold hover:from-teal-700 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    <span>
                      {isSubmitting 
                        ? '提交中...' 
                        : useBlockchain 
                          ? '提交到区块链' 
                          : '发送消息'
                      }
                    </span>
                  </button>
                </form>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  部门联系
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                  根据您的需求，可以直接联系相关部门
                </p>

                <div className="space-y-6">
                  {departments.map((dept, index) => (
                    <motion.div
                      key={dept.name}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-teal-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MessageCircle className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            {dept.name}
                          </h3>
                          <p className="text-teal-600 dark:text-teal-400 font-semibold mb-2">
                            {dept.email}
                          </p>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {dept.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 bg-gradient-to-r from-teal-600 to-blue-600 rounded-2xl p-6 text-white">
                  <h3 className="text-xl font-bold mb-4">在线客服</h3>
                  <p className="mb-4">
                    需要即时帮助？我们的在线客服团队随时为您服务
                  </p>
                  <button className="bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300 flex items-center space-x-2">
                    <Globe className="w-5 h-5" />
                    <span>开始对话</span>
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-20 bg-gray-50 dark:bg-gray-900">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                我们的位置
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                欢迎您到我们的办公室参观交流
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg"
            >
              <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    地图加载中...
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                    北京市朝阳区xxx大厦
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Contact